/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Audit › Trends (docs/CONTRACT-fleet-governance.md section 12.3; owner, D8): how Gyro's rules are holding up over time.
 * The page header holds the range (7, 30 or 90 days, or Custom with two dates) and the app, both kept in the address
 * (`?days=30&app=` or `?from=&to=`), and Settings; eight panels follow, top to bottom. Every number with audit rows behind
 * it opens "Show audit rows" (AuditRowsDrawer). Counts only on this page: no section names a person.
 *
 * `TrendsGate` is what App.tsx renders: the probe's `checking` and `hidden` states as plain pages, `shown` as the page.
 */
import { useCallback, useState } from 'react';
import { Settings } from 'lucide-react';
import { api } from '../../lib/api';
import { filterFromSearch, filterSearch, rangeQuery, type DrillRequest, type RangeDays, type TrendsFilter } from '../../lib/trends';
import { navigate, usePlace } from '../../shell/router';
import { Button, EmptyState, Input, PageHeader, Segmented, Select, Stack } from '../../ui';
import { useLoad } from '../pulse/common';
import { PlainPage } from '../fleet/parts';
import { TrendsTiles } from './Tiles';
import { TrendsDlp } from './Dlp';
import { TrendsRefusals } from './Refusals';
import { TrendsDecisions } from './Decisions';
import { TrendsFunnel } from './Funnel';
import { TrendsReceivers } from './Receivers';
import { TrendsElevation } from './Elevation';
import { TrendsChanges } from './Changes';
import { AuditRowsDrawer } from './AuditRowsDrawer';
import { TrendsSettingsDrawer } from './TrendsSettings';

export function TrendsGate({ state, canEdit }: { state: 'checking' | 'shown' | 'hidden'; canEdit: boolean }) {
  if (state === 'checking') {
    return <PlainPage><EmptyState title="Checking whether Trends is on">The Trends sections appear here once the service answers.</EmptyState></PlainPage>;
  }
  if (state === 'hidden') {
    return (
      <PlainPage>
        <EmptyState title="Trends is not switched on">
          Trends shows how Gyro's rules are holding up over time: DLP blocks, refusals, decisions and approvals. It appears here once the governance-trends flag is turned on under Feature flags.
        </EmptyState>
      </PlainPage>
    );
  }
  return <TrendsPage canEdit={canEdit} />;
}

type RangeChoice = '7' | '30' | '90' | 'custom';
const RANGE_OPTIONS: Array<{ value: RangeChoice; label: string }> = [
  { value: '7', label: '7 days' }, { value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: 'custom', label: 'Custom' },
];
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function TrendsPage({ canEdit }: { canEdit: boolean }) {
  const place = usePlace();
  const filter = filterFromSearch(place.search);
  const query = rangeQuery(filter);
  const [customOpen, setCustomOpen] = useState(filter.days === null);
  const [from, setFrom] = useState(filter.from ?? '');
  const [to, setTo] = useState(filter.to ?? '');
  const [drill, setDrill] = useState<DrillRequest | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [seenApps, setSeenApps] = useState<string[]>([]);
  // The registered apps (IT admins may read them); an auditor's list fills from the apps the sections report.
  const registered = useLoad(() => api.apps().then((answer) => answer.apps.map((app) => app.key), () => [] as string[]), 'apps');

  const setFilter = (next: TrendsFilter) => navigate(`/trends${filterSearch(next)}`, { replace: true });
  const noteApps = useCallback((names: readonly string[]) => {
    setSeenApps((current) => {
      const missing = names.filter((name) => name && !current.includes(name));
      return missing.length ? [...current, ...missing] : current;
    });
  }, []);
  const apps = [...new Set([...(registered.load.state === 'ready' ? registered.load.data : []), ...seenApps, ...(filter.app ? [filter.app] : [])])].sort();

  const choice: RangeChoice = customOpen ? 'custom' : String(filter.days ?? 30) as RangeChoice;
  const pickRange = (value: RangeChoice) => {
    if (value === 'custom') {
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    setFilter({ days: Number(value) as RangeDays, from: null, to: null, app: filter.app });
  };
  const pickDates = (nextFrom: string, nextTo: string) => {
    setFrom(nextFrom);
    setTo(nextTo);
    if (DATE.test(nextFrom) && DATE.test(nextTo) && nextFrom <= nextTo) setFilter({ days: null, from: nextFrom, to: nextTo, app: filter.app });
  };

  const sectionProps = { filter, query, onShowRows: setDrill };

  return (
    <>
      <PageHeader
        meta={<span>How Gyro's rules are holding up over time. Every number opens the audit rows behind it.</span>}
        actions={(
          <>
            <Segmented label="Range" options={RANGE_OPTIONS} value={choice} onChange={pickRange} />
            {customOpen ? (
              <>
                <Input size="sm" type="date" aria-label="From" value={from} onChange={(event) => pickDates(event.target.value, to)} />
                <Input size="sm" type="date" aria-label="To" value={to} onChange={(event) => pickDates(from, event.target.value)} />
              </>
            ) : null}
            <Select size="sm" className="ui-w-auto" aria-label="App" value={filter.app} onChange={(event) => setFilter({ ...filter, app: event.target.value })}>
              <option value="">All apps</option>
              {apps.map((app) => <option key={app} value={app}>{app}</option>)}
            </Select>
            <Button size="sm" icon={Settings} onClick={() => setSettingsOpen(true)}>Settings</Button>
          </>
        )}
      />
      <div className="ui-page">
        <Stack>
          <TrendsTiles {...sectionProps} />
          <TrendsDlp {...sectionProps} onApps={noteApps} />
          <TrendsRefusals {...sectionProps} />
          <TrendsDecisions {...sectionProps} onApps={noteApps} />
          <TrendsFunnel {...sectionProps} />
          <TrendsReceivers {...sectionProps} />
          <TrendsElevation {...sectionProps} />
          <TrendsChanges {...sectionProps} />
        </Stack>
      </div>
      <AuditRowsDrawer request={drill} onClose={() => setDrill(null)} />
      <TrendsSettingsDrawer open={settingsOpen} canEdit={canEdit} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

